import { motion } from 'framer-motion';
import {
  FolderGit2,
  Github,
  Globe,
  AlertCircle,
  CheckCircle2,
  Clock,
  MoreHorizontal,
  Kanban,
  Award,
  ArrowRight,
  Receipt
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';

// Interface pour les sous-projets
interface SubProject {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'in_progress' | 'maintenance' | 'archived';
  type: 'web' | 'api' | 'tool' | 'library';
  url?: string;
  github?: string;
  technologies: string[];
  lastUpdated: string;
  version?: string;
}

// Données de démonstration des sous-projets
const subProjects: SubProject[] = [
  {
    id: '1',
    name: 'Portfolio Admin',
    description: 'Interface d\'administration du portfolio avec Kanban et gestion des projets.',
    status: 'in_progress',
    type: 'web',
    github: 'https://github.com/pierretouzeau/portfolio',
    technologies: ['React', 'TypeScript', 'Firebase', 'Tailwind CSS'],
    lastUpdated: '2026-02-23',
    version: '1.0.0'
  },
  {
    id: '2',
    name: 'API Gateway',
    description: 'Passerelle API pour centraliser les appels aux services externes.',
    status: 'active',
    type: 'api',
    url: 'https://api.pierretouzeau.me',
    github: 'https://github.com/pierretouzeau/api-gateway',
    technologies: ['Node.js', 'Express', 'Docker', 'Nginx'],
    lastUpdated: '2026-02-20',
    version: '2.1.0'
  },
  {
    id: '3',
    name: 'DevOps Scripts',
    description: 'Collection de scripts d\'automatisation pour le déploiement et le monitoring.',
    status: 'active',
    type: 'tool',
    github: 'https://github.com/pierretouzeau/devops-scripts',
    technologies: ['Python', 'Bash', 'Terraform', 'Ansible'],
    lastUpdated: '2026-02-18',
    version: '1.5.2'
  },
  {
    id: '4',
    name: 'Movie Tracker',
    description: 'Application de suivi des films et séries avec intégration Trakt.tv.',
    status: 'maintenance',
    type: 'web',
    url: 'https://movies.pierretouzeau.me',
    github: 'https://github.com/pierretouzeau/movie-tracker',
    technologies: ['React', 'Firebase', 'Trakt API', 'TMDB API'],
    lastUpdated: '2026-01-15',
    version: '1.2.0'
  },
  {
    id: '5',
    name: 'Config Validator',
    description: 'Librairie de validation des fichiers de configuration YAML/JSON.',
    status: 'archived',
    type: 'library',
    github: 'https://github.com/pierretouzeau/config-validator',
    technologies: ['Go', 'Cobra', 'Viper'],
    lastUpdated: '2025-11-30',
    version: '0.9.0'
  }
];

const statusConfig = {
  active: {
    label: 'Actif',
    color: 'bg-[#108548]/10 text-[#108548] border-[#108548]/20',
    icon: CheckCircle2
  },
  in_progress: {
    label: 'En cours',
    color: 'bg-[#1f75cb]/10 text-[#1f75cb] border-[#1f75cb]/20',
    icon: Clock
  },
  maintenance: {
    label: 'Maintenance',
    color: 'bg-[#fc6d26]/10 text-[#fc6d26] border-[#fc6d26]/20',
    icon: AlertCircle
  },
  archived: {
    label: 'Archivé',
    color: 'bg-[#5e5e5e]/10 text-[#5e5e5e] border-[#5e5e5e]/20',
    icon: FolderGit2
  }
};

const typeConfig = {
  web: { label: 'Web App', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  api: { label: 'API', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  tool: { label: 'Outil', color: 'bg-orange-50 text-orange-700 border-orange-200' },
  library: { label: 'Librairie', color: 'bg-pink-50 text-pink-700 border-pink-200' }
};

export function AdminIndex() {
  return (
    <AdminLayout title="Dashboard">
      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Link
          to="/admin/kanban"
          className="card card-hover p-5 flex items-center justify-between group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#1f75cb]/10 rounded-xl flex items-center justify-center">
              <Kanban className="w-6 h-6 text-[#1f75cb]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1d1d1f]">Kanban Board</h3>
              <p className="text-sm text-[#5e5e5e]">Gérer mes tâches</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-[#5e5e5e] group-hover:text-[#1f75cb] group-hover:translate-x-1 transition-all" />
        </Link>
        <Link
          to="/admin/certifications"
          className="card card-hover p-5 flex items-center justify-between group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#fc6d26]/10 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-[#fc6d26]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1d1d1f]">Certifications & Badges</h3>
              <p className="text-sm text-[#5e5e5e]">Gérer mes certifications</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-[#5e5e5e] group-hover:text-[#fc6d26] group-hover:translate-x-1 transition-all" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Link
          to="/admin/expenses"
          className="card card-hover p-5 flex items-center justify-between group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Receipt className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1d1d1f]">Dépenses Fixes</h3>
              <p className="text-sm text-[#5e5e5e]">Suivre mes dépenses</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-[#5e5e5e] group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
        </Link>
      </div>

      {/* Stats Overview */}
      <h2 className="text-lg font-semibold text-[#1d1d1f] mb-4">Vue d'ensemble</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Projets', value: subProjects.length, color: 'text-[#1f75cb]' },
          { label: 'Actifs', value: subProjects.filter(p => p.status === 'active').length, color: 'text-[#108548]' },
          { label: 'En cours', value: subProjects.filter(p => p.status === 'in_progress').length, color: 'text-[#fc6d26]' },
          { label: 'Archivés', value: subProjects.filter(p => p.status === 'archived').length, color: 'text-[#5e5e5e]' },
        ].map((stat, idx) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="card p-4"
          >
            <p className="text-xs text-[#5e5e5e] mb-1">{stat.label}</p>
            <p className={`text-2xl font-semibold ${stat.color}`}>{stat.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {subProjects.map((project, idx) => {
          const StatusIcon = statusConfig[project.status].icon;

          return (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 + 0.2 }}
              className="card card-hover p-5"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#f5f5f7] rounded-lg flex items-center justify-center">
                    <FolderGit2 className="w-5 h-5 text-[#1f75cb]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1d1d1f]">{project.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`badge badge-sm ${typeConfig[project.type].color}`}>
                        {typeConfig[project.type].label}
                      </span>
                      {project.version && (
                        <span className="text-xs text-[#5e5e5e]">v{project.version}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className={`badge ${statusConfig[project.status].color} flex items-center gap-1`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusConfig[project.status].label}
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-[#5e5e5e] mb-4 line-clamp-2">
                {project.description}
              </p>

              {/* Technologies */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {project.technologies.map(tech => (
                  <span
                    key={tech}
                    className="px-2 py-0.5 bg-[#f5f5f7] rounded text-xs text-[#5e5e5e]"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-[#e5e5e5]">
                <span className="text-xs text-[#5e5e5e]">
                  Mis à jour : {new Date(project.lastUpdated).toLocaleDateString('fr-FR')}
                </span>

                <div className="flex items-center gap-2">
                  {project.github && (
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-[#5e5e5e] hover:text-[#1d1d1f] hover:bg-[#f5f5f7] rounded-lg transition-colors"
                      title="Voir sur GitHub"
                    >
                      <Github className="w-4 h-4" />
                    </a>
                  )}
                  {project.url && (
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-[#5e5e5e] hover:text-[#1f75cb] hover:bg-[#1f75cb]/10 rounded-lg transition-colors"
                      title="Ouvrir l'application"
                    >
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    className="p-2 text-[#5e5e5e] hover:text-[#1d1d1f] hover:bg-[#f5f5f7] rounded-lg transition-colors"
                    title="Plus d'options"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Empty State (si aucun projet) */}
      {subProjects.length === 0 && (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 bg-[#f5f5f7] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FolderGit2 className="w-8 h-8 text-[#5e5e5e]" />
          </div>
          <h3 className="text-lg font-medium text-[#1d1d1f] mb-2">Aucun projet</h3>
          <p className="text-sm text-[#5e5e5e]">
            Commencez par créer votre premier sous-projet.
          </p>
        </div>
      )}
    </AdminLayout>
  );
}
